import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, Info, Loader2, Send, Truck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEDULE_LABEL = {
  primary: "Chosen date",
  mon_wed: "Mon-Wed window",
  wed_fri: "Wed-Fri window",
};

const SCHEDULE_DAYS = {
  all_week: new Set([0, 1, 2, 3, 4, 5, 6]),
  mon_wed: new Set([0, 1, 2]),
  wed_fri: new Set([2, 3, 4]),
};

function computeDeliveryGroups(basket, dateStr) {
  if (!basket.length) return [];
  const groups = { primary: [], mon_wed: [], wed_fri: [] };
  let day = null;
  if (dateStr) {
    const jsDay = new Date(dateStr + "T00:00:00").getDay(); // 0=Sun..6=Sat
    day = jsDay === 0 ? 6 : jsDay - 1; // 0=Mon..6=Sun
  }
  for (const b of basket) {
    const sched = b.item.delivery_schedule || "all_week";
    const days = SCHEDULE_DAYS[sched] || SCHEDULE_DAYS.all_week;
    if (day == null || days.has(day)) groups.primary.push(b);
    else if (groups[sched]) groups[sched].push(b);
    else groups.primary.push(b);
  }
  const dayLabel = dateStr ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][day] : null;
  const out = [];
  if (groups.primary.length) {
    out.push({ schedule: "primary", date: dateStr || null, day_hint: dayLabel || "", items: groups.primary });
  }
  ["mon_wed", "wed_fri"].forEach((k) => {
    if (groups[k].length) out.push({ schedule: k, date: null, day_hint: SCHEDULE_LABEL[k], items: groups[k] });
  });
  return out;
}

export default function CheckoutModal({
  open,
  onClose,
  basket,
  total,
  selectedWeek,
  onSuccess,
  locationSlug = null,
  customerProfile = null,
}) {
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-fill from location's customer_profile when modal opens
  useEffect(() => {
    if (!open || !customerProfile) return;
    setFormData((prev) => ({
      ...prev,
      customer_name: prev.customer_name || customerProfile.contact_name || "",
      customer_email: prev.customer_email || customerProfile.email || "",
      customer_phone: prev.customer_phone || customerProfile.phone || "",
      delivery_address: prev.delivery_address || customerProfile.delivery_address || "",
    }));
  }, [open, customerProfile]);

  // Calculate delivery date constraints based on selected week
  const dateConstraints = useMemo(() => {
    if (!selectedWeek) {
      // Fallback: 48-hour minimum with 12pm cutoff
      const now = new Date();
      const currentHour = now.getHours();
      const daysToAdd = currentHour >= 12 ? 3 : 2;
      now.setDate(now.getDate() + daysToAdd);
      return {
        min: now.toISOString().split("T")[0],
        max: null,
        weekLabel: null
      };
    }

    // Restrict to selected week's Mon-Sat
    return {
      min: selectedWeek.week_start,
      max: selectedWeek.week_end,
      weekLabel: `Week ${selectedWeek.week_number}`
    };
  }, [selectedWeek]);

  // Grouped preview based on the chosen delivery date + item schedules
  const deliveryGroups = useMemo(
    () => computeDeliveryGroups(basket, formData.delivery_date),
    [basket, formData.delivery_date]
  );
  const willSplit = deliveryGroups.length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone ||
        !formData.delivery_address || !formData.delivery_date) {
      toast.error("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customer_email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    try {
      const quoteData = {
        ...formData,
        notes: formData.notes?.trim() || null,
        delivery_week: selectedWeek?.week_number || null,
        location_slug: locationSlug,
        items: basket.map(b => ({
          item_id: b.item.id,
          name: b.item.name,
          price: b.variant ? b.variant.price : b.item.price,
          quantity: b.quantity,
          variant: b.variant?.name || null,
          week_number: b.weekNumber || null,
        })),
        total_amount: total,
        delivery_groups: deliveryGroups.map((g) => ({
          schedule: g.schedule,
          date: g.date,
          day_hint: g.day_hint,
          item_ids: g.items.map((b) => b.item.id),
        })),
      };

      await axios.post(`${API}/quotes`, quoteData);
      setSuccess(true);
      
      setTimeout(() => {
        setSuccess(false);
        setFormData({
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          delivery_address: "",
          delivery_date: "",
          notes: "",
        });
        onSuccess();
        onClose();
      }, 2500);
    } catch {
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="py-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Order Placed!</h3>
            <p className="text-gray-500">
              We&apos;ve received your order and will confirm final pricing shortly. Check your email for a copy.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 max-h-[80vh] sm:max-h-[85vh] overflow-hidden">
        <DialogHeader className="p-6 pb-4 flex-shrink-0 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#1aa59a]" />
            Place Order
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 p-6 pt-4" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Step 1 — Delivery date (moved to top) */}
            <div className="border-2 border-[#1aa59a]/30 bg-[#1aa59a]/5 rounded-lg p-3">
              <Label htmlFor="date" className="text-sm font-bold text-[#1aa59a]">
                Step 1 · Preferred Delivery Date *
              </Label>
              <Input
                id="date"
                type="date"
                min={dateConstraints.min}
                max={dateConstraints.max || undefined}
                value={formData.delivery_date}
                onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                required
                data-testid="checkout-date"
                className="block w-full mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {dateConstraints.weekLabel
                  ? `Delivery available ${dateConstraints.weekLabel}: Mon-Sat only`
                  : "Minimum 48 hours notice required (12pm daily cutoff)"}
              </p>
            </div>

            {/* Split-delivery notice — between Step 1 and Delivery Summary */}
            <div
              className="flex gap-2 items-start bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900"
              data-testid="split-delivery-notice"
            >
              <Truck className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
              <p className="text-xs sm:text-sm leading-snug">
                Some items are only made on specific days. If your basket spans multiple delivery
                windows we&apos;ll split it into separate deliveries and contact you to confirm the
                Mon-Wed or Wed-Fri slot.
              </p>
            </div>

            {/* Delivery grouping preview */}
            {deliveryGroups.length > 0 && (
              <div
                className="bg-gray-50 rounded-lg p-4 space-y-3"
                data-testid="delivery-groups-preview"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-800 text-sm">
                    {willSplit ? "Your order will be split into 2 deliveries" : "Delivery summary"}
                  </h4>
                  {willSplit && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Split
                    </span>
                  )}
                </div>

                {deliveryGroups.map((g, idx) => (
                  <div
                    key={g.schedule}
                    className={`rounded-lg p-3 border ${
                      g.schedule === "primary"
                        ? "bg-white border-[#1aa59a]/30"
                        : "bg-amber-50 border-amber-200"
                    }`}
                    data-testid={`delivery-group-${g.schedule}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold text-slate-700">
                        Delivery {idx + 1}
                      </span>
                      {g.schedule === "primary" ? (
                        <span className="text-xs text-[#1aa59a]">
                          · {formData.delivery_date ? new Date(formData.delivery_date + "T00:00:00").toDateString() : "Chosen date"}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700">
                          · {g.day_hint} — date TBC (we&apos;ll confirm)
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {g.items.map((b) => (
                        <div
                          key={`${b.item.id}-${b.variant?.name || "base"}`}
                          className="flex justify-between text-xs text-gray-700"
                        >
                          <span>
                            {b.quantity}× {b.item.name}
                            {b.variant && <span className="text-[#1aa59a]"> ({b.variant.name})</span>}
                          </span>
                          <span className="font-medium">
                            £{((b.variant ? b.variant.price : b.item.price) * b.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-[#1aa59a]" data-testid="checkout-total">
                    £{total.toFixed(2)}
                  </span>
                </div>

                {/* Notices below pricing total */}
                <div
                  className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900"
                  data-testid="indicative-price-notice"
                >
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
                  <p className="text-xs sm:text-sm leading-snug">
                    Prices shown are <strong>indicative</strong> and may change depending on site,
                    order volume, delivery location and other factors. We&apos;ll confirm the final
                    total before dispatch.
                  </p>
                </div>
                <p
                  className="text-[11px] sm:text-xs text-gray-500 text-center"
                  data-testid="shipping-tax-notice"
                >
                  Pricing does not include shipping or taxes.
                </p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="+44 7123 456789"
                  required
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <Label htmlFor="address">Delivery Address *</Label>
                <Textarea
                  id="address"
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  placeholder="123 Main Street, City, Postcode"
                  rows={2}
                  required
                  data-testid="checkout-address"
                />
              </div>

              <div>
                <Label htmlFor="notes">Order Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Allergies, dietary requirements, delivery instructions, or anything else we should know..."
                  rows={4}
                  maxLength={1500}
                  data-testid="checkout-notes"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Anything specific about this order — allergies, access instructions, gate codes, etc.
                </p>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1aa59a] hover:bg-[#158a80] text-white py-6 text-lg rounded-xl mt-4"
              data-testid="submit-quote-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Placing order...
                </>
              ) : (
                "Place Order"
              )}
            </Button>
            
            {/* Safe area padding for mobile */}
            <div className="h-16 sm:h-4"></div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
